import React, { useState } from 'react';
import { Divider, Text, Toggle } from '@spark';
import Hyperparams from './Hyperparams.jsx';
import HardwareDeviceSelector from './HardwareDeviceSelector.jsx';
import RuntimeStrategySelector from './RuntimeStrategySelector.jsx';
import { isGenerativeTask } from '@/shared/domain/taskTypes';
import { isNerTaskType } from '@/features/trainings/constants';

/**
 * Advanced training options accordion (Figma 3001-68512 / 3121-6533).
 * Toggle in the header expands Training Device, Metric Optimisation, and
 * generative-specific controls — matching the Train / Model Summary layout.
 * Prediction Strategy is classification-only (hidden for NER). Base model
 * selection is shown for every task type. "Optimize on"
 * (`rareClassLabelOptions`) appears only for two-label single-label
 * classification tasks.
 *
 * Whether the student prompts are included in training & evaluation is now
 * configured on Model Configuration > SpecificAI model ("Disable" per field),
 * not here.
 */
const Advanced = (props) => {
    const [open, setOpen] = useState(false);
    const isGenerative = isGenerativeTask(props.taskType);

    return (
        <div className="train-advanced fs-unmask">
            <div className="train-advanced__header">
                <Text variant="lead-semibold" as="div">
                    Advanced
                </Text>
                <Toggle
                    active={open}
                    onChange={(e) => setOpen(e.target.checked)}
                    aria-label="Advanced training options"
                />
            </div>

            {open && (
                <div className="train-advanced__body">
                    {!isGenerative && (
                        <HardwareDeviceSelector
                            trainingDevice={props.trainingDevice}
                            onHardwareDeviceChange={props.onHardwareDeviceChange}
                        />
                    )}
                    {!isGenerative && (
                        <Hyperparams
                            metricOptimization={props.metricOptimization}
                            onMetricOptimizationChange={props.onMetricOptimizationChange}
                            betaForFScore={props.betaForFScore}
                            onFScoreBetaChange={props.onFScoreBetaChange}
                            rareClassLabel={props.rareClassLabel}
                            onRareClassLabelChange={props.onRareClassLabelChange}
                            rareClassLabelOptions={props.rareClassLabelOptions}
                        />
                    )}
                    {isGenerative && props.accuracyMetricsSelector && (
                        <>
                            {props.accuracyMetricsSelector}
                            <Divider />
                        </>
                    )}
                    {isGenerative && props.judgeCriteriaSelector}
                    {isGenerative && props.classicMetricsSelector && (
                        <>
                            <Divider />
                            {props.classicMetricsSelector}
                        </>
                    )}
                    {!isGenerative && !isNerTaskType(props.taskType) && (
                        <RuntimeStrategySelector
                            predictionStrategy={props.predictionStrategy}
                            onPredictionStrategyChange={props.onPredictionStrategyChange}
                        />
                    )}
                    {props.baseModelSelector && (
                        <>
                            <Divider />
                            {props.baseModelSelector}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default Advanced;
